/**
 * Organizational Interviews - People Readiness Assessment
 *
 * Per design doc: Organizational_Level_Features_Design.md
 * Users UPLOAD interview transcripts at the organization level.
 * Transcripts are analyzed for sentiment to calculate People Readiness score.
 *
 * Key features:
 * - Upload interview transcripts (text content)
 * - Multi-select interviews for batch analysis
 * - Analyze sentiment and generate recommendations
 * - Save recommendations with confirmation
 * - Wire sentiment to Bridge AI
 *
 * @version 3.0.0 - Feature parity with station-level interviews.js
 * @date 2025-12-04
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SentimentDashboard } from '../../components/SentimentDashboard';
import { OrgSentimentHeatmap } from '../../components/OrgSentimentHeatmap';
import { DomainSelect } from '../../components/triangulation/DomainSelect';
import './governance-layout.css';
import './organizational-interviews.css';

// Interview type from API
interface Interview {
  interview_id: string;
  interviewee_role: string;
  interviewee_name: string | null;
  interview_date: string;
  sentiment_score: number | null;
  overall_emotion: string | null;
  statement_count: number | null;
  uploaded_at: string;
  analyzed_at: string | null;
}

// Full interview detail with sentiment analysis
interface InterviewDetail extends Interview {
  transcript_text?: string;
  emotion_scores?: Record<string, number>;
  fair_heatmap?: {
    by_pillar: Record<string, {
      overall_sentiment: number;
      by_persona?: Record<string, number>;
      by_role?: Record<string, number>;
      quotes: Array<{ text: string; sentiment: number; persona?: string }>;
    }>;
  };
}

// Aggregate stats from API
interface InterviewAggregate {
  total_interviews: number;
  analyzed_interviews: number;
  people_readiness_score: number | null;
  roles: string[];
  last_updated: string | null;
}

// Recommendation from analysis
interface Recommendation {
  recommendation_id: string;
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  impact: string;
  effort: string;
  fair_pillars: string[];
  pistoia_driver?: {
    driver_id: string;
    driver_name: string;
    category: string;
  };
}

// Sentiment analysis types matching SentimentDashboard expectations
interface EmotionScore {
  emotion: string;
  percentage: number;
  count: number;
}

interface SentimentQuote {
  text: string;
  sentiment: number;
}

interface FairPillarSentiment {
  overall_sentiment: number;
  by_persona: Record<string, number>;
  quotes: SentimentQuote[];
}

interface SentimentAnalysisData {
  emotions: {
    overall_emotion: string;
    emotion_scores: Record<string, number>;
    emotion_distribution: EmotionScore[];
  };
  fair_heatmap: {
    by_pillar: Record<string, FairPillarSentiment>;
  };
  insights: {
    biggest_pain_point: string;
    biggest_win: string;
    emotional_intensity: number;
    consensus_level: number;
  };
  metadata: {
    total_statements: number;
    analyzed_at: string;
    model: string;
  };
}

// Analysis result from API
interface AnalysisResult {
  people_readiness_score: number;
  recommendations_generated: number;
  analysis: SentimentAnalysisData;
}

export const OrganizationalInterviews: React.FC = () => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [aggregate, setAggregate] = useState<InterviewAggregate | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Multi-select state (following interviews.js pattern)
  const [selectedInterviews, setSelectedInterviews] = useState<Set<string>>(new Set());

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [intervieweeRole, setIntervieweeRole] = useState('');
  const [intervieweeName, setIntervieweeName] = useState('');
  // v1.2.3 — interviewer name feeds speaker attribution for Teams real-name
  // transcripts (the parser excludes the interviewer's turns by name).
  const [interviewerName, setInterviewerName] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  // DC-1493 — optional triangulation domain assignment at upload time.
  const [domainId, setDomainId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFormRef = useRef<HTMLDivElement>(null);

  // Scroll the upload form into view when it opens — without this, clicking
  // "+ Upload Interview" appears to do nothing because the form renders below
  // the summary cards + sentiment heatmap, off the viewport.
  useEffect(() => {
    if (showUploadForm && uploadFormRef.current) {
      uploadFormRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
     
  }, [showUploadForm]);

  // Interview detail view state
  const [viewingInterview, setViewingInterview] = useState<InterviewDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [viewMode, setViewMode] = useState<'analysis' | 'transcript'>('analysis');

  // Load interviews on mount
  useEffect(() => {
    loadInterviews();
  }, []);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Organizational_Interviews_User_Guide.md' })
      });
    } catch (err) {
      console.error('[OrganizationalInterviews] Error opening guide:', err);
    }
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadInterviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/organization/interviews');
      if (!response.ok) {
        throw new Error(`Failed to load interviews: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setInterviews(data.interviews || []);
        setAggregate(data.aggregate || null);
      } else {
        throw new Error(data.error || 'Failed to load interviews');
      }
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] Load error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection for transcript upload
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // PDF and Word documents go through the server-side extractor
      // (pdf-parse / mammoth). v1.2.3: .docx is Teams' native transcript
      // download format — first-class, not read as raw text.
      const lower = file.name.toLowerCase();
      if (
        file.type === 'application/pdf' || lower.endsWith('.pdf') ||
        lower.endsWith('.docx') || file.type.includes('officedocument') || file.type.includes('msword')
      ) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/upload-pdf', {
          method: 'POST',
          body: formData,
          credentials: 'include', // /api/upload-pdf is auth-gated in v1.2; send the session cookie
        });

        if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again before uploading the transcript.');
        }
        if (!response.ok) {
          // v1.2.3 — surface the server's named cause (NOT_A_PDF,
          // PDF_ENCRYPTED, FILE_TOO_LARGE, …) instead of a generic line.
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error?.message || body.message || `Failed to parse PDF (HTTP ${response.status})`);
        }

        const data = await response.json();
        if (data.error) {
          throw new Error(data.error.message || 'PDF parsing failed');
        }

        if (!data.text || data.text.length < 50) {
          setError('The file has very little extractable text (a scanned PDF, perhaps). Please use a text-based export or paste the transcript manually.');
          return;
        }

        setTranscriptText(data.text);
      } else {
        // For text files, read directly
        const content = await file.text();
        // v1.2.3 — binary guard: a non-text file (e.g. .docx renamed, or a
        // wrong picker choice) decodes to NULs / replacement chars. Catch it
        // here with a descriptive error instead of silently loading garbage.
        const sample = content.slice(0, 2000);
        const junk = (sample.match(/[\u0000�]/g) || []).length;
        if (junk > 5) {
          setError(`"${file.name}" looks like a binary file, not a text transcript. Upload a PDF/TXT/MD/VTT export, or paste the transcript text directly.`);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        setTranscriptText(content);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError('Failed to read file: ' + message);
    }
  }, []);

  // Upload interview transcript
  const handleUpload = useCallback(async () => {
    if (!transcriptText.trim() || !intervieweeRole.trim()) {
      setError('Transcript text and interviewee role are required');
      return;
    }
    if (!domainId) {
      // DC-1619 — domain is required at creation (Exploratory is the catch-all).
      setError('Please select a domain for this interview.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await fetch('/api/organization/interviews/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript_text: transcriptText,
          interviewee_role: intervieweeRole,
          interviewee_name: intervieweeName || null,
          interviewer: interviewerName || null,
          interview_date: interviewDate || new Date().toISOString().split('T')[0],
          domain_id: domainId,
        }),
      });

      if (!response.ok) {
        // v1.2.3 — surface the server's descriptive message (e.g. the 422
        // TRANSCRIPT_FORMAT_UNRECOGNIZED with supported shapes) instead of
        // the bare status text.
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        // Reset form and reload
        setTranscriptText('');
        setIntervieweeRole('');
        setIntervieweeName('');
        setInterviewerName('');
        setInterviewDate('');
        setDomainId(null);
        setShowUploadForm(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        // v1.2.3 — show what the analyzer will see, including any excluded
        // co-speakers, so mis-attribution surfaces NOW instead of after
        // analysis.
        const preview = data.parse_preview as
          | { statement_count: number; excluded_speakers: string[]; warnings: string[] }
          | undefined;
        if (preview) {
          let msg = `Interview uploaded — ${preview.statement_count} statement${preview.statement_count === 1 ? '' : 's'} parsed.`;
          if (preview.excluded_speakers.length > 0) {
            msg += ` Attribution note: excluded co-speaker(s) ${preview.excluded_speakers.join(', ')} — if that's the wrong person, re-upload with the Name/Interviewer fields filled in.`;
          }
          setSuccessMessage(msg);
        } else {
          setSuccessMessage('Interview uploaded successfully!');
        }
        await loadInterviews();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] Upload error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setUploading(false);
    }
  }, [transcriptText, intervieweeRole, intervieweeName, interviewerName, interviewDate, domainId]);

  // Analyze selected interviews (following interviews.js:500-588 pattern)
  const handleAnalyze = useCallback(async () => {
    const selectedIds = Array.from(selectedInterviews);

    if (selectedIds.length === 0) {
      setError('Please select at least one interview to analyze');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);
      setAnalysisResult(null);
      setRecommendations([]);

      console.log(`[OrganizationalInterviews] Analyzing ${selectedIds.length} interviews`);

      const response = await fetch('/api/organization/interviews/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interview_ids: selectedIds,
        }),
      });

      if (!response.ok) {
        // Surface the server's message (e.g. the 503 NLP_UNAVAILABLE
        // "Verify the Azure OpenAI configuration and retry") instead of the
        // bare status text (INT-6).
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setAnalysisResult({
          people_readiness_score: data.people_readiness_score,
          recommendations_generated: data.recommendations_generated || 0,
          analysis: data.analysis as SentimentAnalysisData,
        });

        // Load recommendations
        await loadRecommendations();
        await loadInterviews();

        // v1.2.3 — surface per-interview parse diagnostics (skipped
        // unparseable transcripts, speaker-attribution notes).
        const warningList = (data.warnings || []) as Array<{ interview_id: string; warning: string }>;
        let msg = `Analyzed ${data.per_interview_analyses?.length ?? selectedIds.length} interview(s). Generated ${data.recommendations_generated || 0} recommendations.`;
        if (warningList.length > 0) {
          msg += ` ${warningList.length} warning(s): ${warningList.map((w) => w.warning).join(' • ')}`;
        }
        setSuccessMessage(msg);
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] Analyze error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setAnalyzing(false);
    }
  }, [selectedInterviews]);

  // Load recommendations from API
  const loadRecommendations = async () => {
    try {
      const response = await fetch('/api/organization/recommendations?status=active');
      if (!response.ok) return;

      const data = await response.json();
      if (data.success && data.recommendations) {
        setRecommendations(data.recommendations);
      }
    } catch (err) {
      console.error('[OrganizationalInterviews] Load recommendations error:', err);
    }
  };

  // Save recommendations (confirm they are saved)
  const handleSaveRecommendations = useCallback(async () => {
    if (recommendations.length === 0) {
      setError('No recommendations to save. Please analyze interviews first.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Recommendations are already saved during analysis,
      // but we'll update status and send sentiment to Bridge
      const response = await fetch('/api/organization/recommendations/confirm-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recommendation_ids: recommendations.map(r => r.recommendation_id),
          sentiment_data: analysisResult?.analysis,
        }),
      });

      if (!response.ok) {
        // Fallback: recommendations are already saved
        console.log('[OrganizationalInterviews] Confirm endpoint not available, recommendations already saved');
      }

      setSuccessMessage(`Saved ${recommendations.length} recommendations to organization. Sentiment data sent to Bridge AI.`);

    } catch (err: unknown) {
      // Non-blocking - recommendations are already saved during analysis
      console.warn('[OrganizationalInterviews] Save confirmation error:', err);
      setSuccessMessage(`${recommendations.length} recommendations are saved. Review them in Bridge AI.`);
    } finally {
      setSaving(false);
    }
  }, [recommendations, analysisResult]);

  // Delete interview
  const handleDelete = useCallback(async (interviewId: string) => {
    if (!confirm('Delete this interview?')) return;

    try {
      const response = await fetch(`/api/organization/interviews/${interviewId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      // Remove from selection if selected
      setSelectedInterviews(prev => {
        const next = new Set(prev);
        next.delete(interviewId);
        return next;
      });

      await loadInterviews();
      setSuccessMessage('Interview deleted.');
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] Delete error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }, []);

  // View interview detail with sentiment analysis
  const handleViewDetail = useCallback(async (interviewId: string, mode: 'analysis' | 'transcript' = 'analysis') => {
    setLoadingDetail(true);
    setViewMode(mode);
    try {
      const response = await fetch(`/api/organization/interviews/${interviewId}`);
      if (!response.ok) {
        throw new Error(`Failed to load interview: ${response.statusText}`);
      }
      const data = await response.json();
      setViewingInterview(data.interview);
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] View detail error:', err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Close interview detail view
  const handleCloseDetail = useCallback(() => {
    setViewingInterview(null);
  }, []);

  // Export the anonymized analysis PDF for the interview open in the modal.
  // The server builds the report from persona/role only (no interviewee
  // names) — it's the artifact consultants hand back to interviewees.
  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportPdf = useCallback(async (interviewId: string) => {
    setExportingPdf(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/organization/interviews/${interviewId}/report?format=pdf`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error?.message || `Failed to export PDF (HTTP ${response.status})`,
        );
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'Interview_Analysis.pdf';
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      console.error('[OrganizationalInterviews] Export PDF error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setExportingPdf(false);
    }
  }, []);

  // Toggle interview selection (following interviews.js:258-269 pattern)
  const handleCheckboxChange = useCallback((interviewId: string, checked: boolean) => {
    setSelectedInterviews(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(interviewId);
      } else {
        next.delete(interviewId);
      }
      return next;
    });
  }, []);

  // Toggle select all (following interviews.js:317-326 pattern)
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedInterviews(new Set(interviews.map(i => i.interview_id)));
    } else {
      setSelectedInterviews(new Set());
    }
  }, [interviews]);

  // Check if all interviews are selected
  const allSelected = interviews.length > 0 && selectedInterviews.size === interviews.length;
  const someSelected = selectedInterviews.size > 0 && selectedInterviews.size < interviews.length;

  // Get sentiment badge
  const getSentimentBadge = (score: number | null): { label: string; class: string } => {
    if (score === null) return { label: 'Not Analyzed', class: 'badge--pending' };
    if (score >= 75) return { label: 'Positive', class: 'badge--success' };
    if (score >= 50) return { label: 'Mixed', class: 'badge--warning' };
    return { label: 'Concerned', class: 'badge--error' };
  };

  // Get priority badge class
  const getPriorityClass = (priority: string): string => {
    switch (priority) {
      case 'critical': return 'priority--critical';
      case 'high': return 'priority--high';
      case 'medium': return 'priority--medium';
      case 'low': return 'priority--low';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="governance-loading">
        <div className="spinner" />
        <p>Loading organizational interviews...</p>
      </div>
    );
  }

  return (
    <div className="governance-section organizational-interviews">
      {/* Header */}
      <div className="governance-section__header">
        <div className="governance-section__title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2>Organizational Interviews</h2>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open Organizational Interviews User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: 'var(--tri-teal)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: 'var(--tri-teal-tint)',
                border: '1px solid var(--tri-teal-tint-line)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tri-teal-tint-line)';
                e.currentTarget.style.borderColor = 'var(--tri-teal)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--tri-teal-tint)';
                e.currentTarget.style.borderColor = 'var(--tri-teal-tint-line)';
              }}
            >
              ?
            </a>
          </div>
          <p className="governance-section__subtitle">
            Upload stakeholder interview transcripts to measure People Readiness
          </p>
        </div>
        <div className="governance-section__actions">
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="btn btn--primary"
          >
            + Upload Interview
          </button>
          <button
            onClick={handleAnalyze}
            disabled={analyzing || selectedInterviews.size === 0}
            className="btn btn--secondary"
          >
            {analyzing
              ? 'Analyzing...'
              : selectedInterviews.size > 0
                ? `Analyze & Generate Recommendations (${selectedInterviews.size})`
                : 'Select Interviews to Analyze'}
          </button>
          {recommendations.length > 0 && (
            <button
              onClick={handleSaveRecommendations}
              disabled={saving}
              className="btn btn--success"
            >
              {saving ? 'Saving...' : `Save ${recommendations.length} Recommendations`}
            </button>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="governance-success">
          <span className="governance-success__icon">✓</span>
          <span className="governance-success__message">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="governance-success__dismiss">
            ×
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="governance-error">
          <span className="governance-error__icon">!</span>
          <span className="governance-error__message">{error}</span>
          <button onClick={() => setError(null)} className="governance-error__dismiss">
            ×
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="governance-summary">
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.total_interviews || 0}</div>
          <div className="summary-card__label">Total Interviews</div>
        </div>
        <div className="summary-card">
          <div className="summary-card__value">{selectedInterviews.size}</div>
          <div className="summary-card__label">Selected</div>
        </div>
        <div className="summary-card">
          <div className="summary-card__value">{aggregate?.analyzed_interviews || 0}</div>
          <div className="summary-card__label">Analyzed</div>
        </div>
        <div className="summary-card summary-card--primary">
          <div className="summary-card__value">
            {aggregate?.people_readiness_score != null
              ? `${Math.round(aggregate.people_readiness_score)}%`
              : '--'}
          </div>
          <div className="summary-card__label">People Readiness</div>
        </div>
      </div>

      {/* Organization-Wide Sentiment Heatmap */}
      {aggregate && aggregate.analyzed_interviews > 0 && (
        <div className="governance-heatmap-section">
          <OrgSentimentHeatmap />
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div ref={uploadFormRef} className="governance-upload-form">
          <h3>Upload Interview Transcript</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Interviewee Role *</label>
              <select
                value={intervieweeRole}
                onChange={e => setIntervieweeRole(e.target.value)}
                className="form-input"
              >
                <option value="">Select role...</option>
                {/* Pistoia Alliance Personas */}
                <option value="Business Analyst">Business Analyst</option>
                <option value="Business Leader">Business Leader</option>
                <option value="Clinical Data Manager">Clinical Data Manager</option>
                <option value="Computational Scientist">Computational Scientist</option>
                <option value="Data Engineer">Data Engineer</option>
                <option value="Data Governance Expert">Data Governance Expert</option>
                <option value="Data Librarian">Data Librarian</option>
                <option value="Data Owner">Data Owner</option>
                <option value="Data Quality Specialist">Data Quality Specialist</option>
                <option value="Data Scientist">Data Scientist</option>
                <option value="Data Steward">Data Steward</option>
                <option value="Data Strategy Owner">Data Strategy Owner</option>
                <option value="Document Owner">Document Owner</option>
                <option value="FAIR Data Architect">FAIR Data Architect</option>
                <option value="IT Security Officer">IT Security Officer</option>
                <option value="Lab Manager">Lab Manager</option>
                <option value="Legal Data Expert">Legal Data Expert</option>
                <option value="Ontologist">Ontologist</option>
                <option value="Regulatory Affairs Specialist">Regulatory Affairs Specialist</option>
                <option value="Repository Manager">Repository Manager</option>
                <option value="Researcher">Researcher</option>
                <option value="Subject Matter Expert">Subject Matter Expert</option>
                {/* Additional roles */}
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Interviewee Name (optional)</label>
              <input
                type="text"
                value={intervieweeName}
                onChange={e => setIntervieweeName(e.target.value)}
                placeholder="e.g., John Smith"
                className="form-input"
              />
              <small className="form-hint">For Teams transcripts with real names, this picks the right speaker.</small>
            </div>
            <div className="form-group">
              <label>Interviewer (optional)</label>
              <input
                type="text"
                value={interviewerName}
                onChange={e => setInterviewerName(e.target.value)}
                placeholder="e.g., the consultant's name"
                className="form-input"
              />
              <small className="form-hint">The interviewer's turns are excluded from sentiment.</small>
            </div>
            <div className="form-group">
              <label>Interview Date</label>
              <input
                type="date"
                value={interviewDate}
                onChange={e => setInterviewDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <DomainSelect
                required
                value={domainId}
                onChange={setDomainId}
                helpText="Required. The domain this interview belongs to — it feeds that domain's Triangulation View. Choose Exploratory for ad-hoc conversations."
              />
            </div>
            <div className="form-group">
              <label>Upload Transcript File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.vtt,.pdf,.docx,application/pdf"
                onChange={handleFileSelect}
                className="form-input"
              />
              <small className="form-hint">Supports DOCX (Teams transcript download), PDF, TXT, MD, VTT files</small>
            </div>
          </div>
          <div className="form-group form-group--full">
            <label>Transcript Text *</label>
            <textarea
              value={transcriptText}
              onChange={e => setTranscriptText(e.target.value)}
              placeholder="Paste the interview transcript here, or upload a file above..."
              rows={8}
              className="form-textarea"
            />
          </div>
          <div className="form-actions">
            <button
              onClick={handleUpload}
              disabled={uploading || !transcriptText.trim() || !intervieweeRole}
              className="btn btn--primary"
            >
              {uploading ? 'Uploading...' : 'Upload Interview'}
            </button>
            <button
              onClick={() => setShowUploadForm(false)}
              className="btn btn--secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Recommendations Section (shown after analysis) */}
      {recommendations.length > 0 && (
        <div className="governance-recommendations">
          <div className="recommendations-header">
            <h3>Generated Recommendations ({recommendations.length})</h3>
            <p className="recommendations-subtitle">
              These recommendations are mapped to Pistoia drivers for value calculation
            </p>
          </div>
          <div className="recommendations-grid">
            {recommendations.slice(0, 6).map((rec, index) => (
              <div key={rec.recommendation_id || index} className="recommendation-card">
                <div className="recommendation-card__header">
                  <span className={`recommendation-priority ${getPriorityClass(rec.priority)}`}>
                    {rec.priority.toUpperCase()}
                  </span>
                  {rec.pistoia_driver && (
                    <span className="recommendation-driver">
                      {rec.pistoia_driver.category}
                    </span>
                  )}
                </div>
                <h4 className="recommendation-card__title">{rec.title}</h4>
                <p className="recommendation-card__description">{rec.description}</p>
                <div className="recommendation-card__footer">
                  <span className="recommendation-meta">Impact: {rec.impact || 'Medium'}</span>
                  <span className="recommendation-meta">Effort: {rec.effort || 'Medium'}</span>
                </div>
              </div>
            ))}
          </div>
          {recommendations.length > 6 && (
            <p className="recommendations-more">
              + {recommendations.length - 6} more recommendations
            </p>
          )}
        </div>
      )}

      {/* Sentiment Dashboard (if analysis available) */}
      {analysisResult && (
        <div className="governance-analysis-result">
          <SentimentDashboard
            sentiment={analysisResult.analysis}
          />
        </div>
      )}

      {/* Interview List with Multi-Select */}
      <div className="governance-list">
        <div className="list-header">
          <h3>Uploaded Interviews</h3>
          {interviews.length > 0 && (
            <span className="list-count">
              {selectedInterviews.size} of {interviews.length} selected
            </span>
          )}
        </div>
        {interviews.length === 0 ? (
          <div className="governance-empty">
            <p>No interviews uploaded yet.</p>
            <p className="governance-empty__hint">
              Click "Upload Interview" to add stakeholder interview transcripts.
              Interviews are analyzed for sentiment to calculate your organization's
              People Readiness score.
            </p>
          </div>
        ) : (
          <table className="governance-table governance-table--selectable">
            <thead>
              <tr>
                <th className="th-checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={e => handleSelectAll(e.target.checked)}
                    title="Select all interviews"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th>Role</th>
                <th>Name</th>
                <th>Date</th>
                <th>Sentiment</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {interviews.map((interview, index) => {
                const badge = getSentimentBadge(interview.sentiment_score);
                const isSelected = selectedInterviews.has(interview.interview_id);
                return (
                  <tr
                    key={interview.interview_id}
                    className={isSelected ? 'row-selected' : ''}
                    data-testid={`interview-row-${index}`}
                  >
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => handleCheckboxChange(interview.interview_id, e.target.checked)}
                        data-testid={`interview-checkbox-${index}`}
                      />
                    </td>
                    <td>
                      <strong>{interview.interviewee_role}</strong>
                    </td>
                    <td>{interview.interviewee_name || '--'}</td>
                    <td>
                      {interview.interview_date
                        ? new Date(interview.interview_date).toLocaleDateString()
                        : '--'}
                    </td>
                    <td>
                      <span className={`status-badge ${badge.class}`}>
                        {interview.sentiment_score != null
                          ? `${Math.round(interview.sentiment_score)}% - ${badge.label}`
                          : badge.label}
                      </span>
                    </td>
                    <td>
                      {new Date(interview.uploaded_at).toLocaleDateString()}
                    </td>
                    <td>
                      <select
                        className="action-dropdown"
                        value=""
                        onChange={(e) => {
                          const action = e.target.value;
                          if (action === 'view-analysis') {
                            handleViewDetail(interview.interview_id, 'analysis');
                          } else if (action === 'view-transcript') {
                            handleViewDetail(interview.interview_id, 'transcript');
                          } else if (action === 'delete') {
                            handleDelete(interview.interview_id);
                          }
                          e.target.value = ''; // Reset dropdown
                        }}
                        data-testid={`action-dropdown-${interview.interview_id}`}
                      >
                        <option value="">Actions</option>
                        <option value="view-transcript">View Transcript</option>
                        <option value="view-analysis" disabled={interview.sentiment_score == null}>
                          View Analysis
                        </option>
                        <option value="delete">Delete</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Help Section */}
      <div className="governance-help">
        <h4>About People Readiness</h4>
        <p>
          People Readiness measures your organization's stakeholder sentiment and preparedness
          for data initiatives. It contributes <strong>40%</strong> to the Execution Risk coefficient.
        </p>
        <p>
          Upload interview transcripts from stakeholders such as CDOs, Data Scientists, IT Leaders,
          and Research Directors. The sentiment analysis identifies emotional patterns and concerns
          related to FAIR data principles.
        </p>
        <p>
          <strong>Workflow:</strong> Upload → Select → Analyze → Save Recommendations → View in Bridge AI
        </p>
      </div>

      {/* Interview Detail Modal */}
      {viewingInterview && (
        <div className="modal-overlay" onClick={handleCloseDetail}>
          <div className="modal-content modal-content--large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {viewMode === 'transcript' ? 'Interview Transcript' : 'Interview Analysis'}:
                {' '}{viewingInterview.interviewee_role}
              </h2>
              <div className="modal-header-actions">
                {/* Export the anonymized analysis PDF (Analysis view only) */}
                {viewMode === 'analysis' && viewingInterview.fair_heatmap && (
                  <button
                    className="modal-export-btn"
                    onClick={() => handleExportPdf(viewingInterview.interview_id)}
                    disabled={exportingPdf}
                    data-testid="export-analysis-pdf"
                  >
                    {exportingPdf ? 'Exporting…' : 'Export PDF'}
                  </button>
                )}
                {/* Toggle between transcript and analysis views */}
                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn ${viewMode === 'transcript' ? 'active' : ''}`}
                    onClick={() => setViewMode('transcript')}
                  >
                    Transcript
                  </button>
                  <button
                    className={`view-toggle-btn ${viewMode === 'analysis' ? 'active' : ''}`}
                    onClick={() => setViewMode('analysis')}
                    disabled={viewingInterview.sentiment_score == null}
                    title={viewingInterview.sentiment_score == null ? 'Analysis not available - run analysis first' : ''}
                  >
                    Analysis
                  </button>
                </div>
                <button className="modal-close" onClick={handleCloseDetail} aria-label="Close">
                  ×
                </button>
              </div>
            </div>
            <div className="modal-body">
              {/* Interview metadata */}
              <div className="interview-detail-meta">
                <div className="meta-item">
                  <span className="meta-label">Interviewee:</span>
                  <span className="meta-value">
                    {viewingInterview.interviewee_name || viewingInterview.interviewee_role}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Role:</span>
                  <span className="meta-value">{viewingInterview.interviewee_role}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Date:</span>
                  <span className="meta-value">
                    {viewingInterview.interview_date
                      ? new Date(viewingInterview.interview_date).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Statements:</span>
                  <span className="meta-value">{viewingInterview.statement_count || 'N/A'}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Overall Emotion:</span>
                  <span className="meta-value">{viewingInterview.overall_emotion || 'N/A'}</span>
                </div>
              </div>

              {/* Transcript View */}
              {viewMode === 'transcript' && (
                <div className="transcript-view">
                  {viewingInterview.transcript_text ? (
                    <pre className="transcript-content">{viewingInterview.transcript_text}</pre>
                  ) : (
                    <div className="no-transcript-message">
                      <p>Transcript text not available.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Analysis View - Sentiment Dashboard */}
              {viewMode === 'analysis' && (
                <>
                  {viewingInterview.fair_heatmap && viewingInterview.emotion_scores ? (
                    <SentimentDashboard
                      sentiment={{
                        emotions: {
                          overall_emotion: viewingInterview.overall_emotion || 'neutral',
                          // emotion_distribution intentionally omitted — the
                          // dashboard derives percentages from emotion_scores;
                          // the old synthetic per-emotion counts are gone.
                          emotion_scores: viewingInterview.emotion_scores
                        },
                        fair_heatmap: {
                          by_pillar: Object.fromEntries(
                            Object.entries(viewingInterview.fair_heatmap.by_pillar).map(([pillar, data]) => [
                              pillar,
                              {
                                overall_sentiment: data.overall_sentiment,
                                by_persona: data.by_persona || {},
                                quotes: data.quotes
                              }
                            ])
                          )
                        },
                        // Only real, derivable insights — the fabricated
                        // emotional_intensity/consensus_level constants were
                        // dropped in the v2.0 rework.
                        insights: {
                          biggest_pain_point: getBiggestPainPoint(viewingInterview.fair_heatmap),
                          biggest_win: getBiggestWin(viewingInterview.fair_heatmap)
                        },
                        metadata: {
                          total_statements: viewingInterview.statement_count || 0,
                          analyzed_at: viewingInterview.analyzed_at || new Date().toISOString(),
                          model: 'gpt-4o-mini'
                        }
                      }}
                    />
                  ) : (
                    <div className="no-analysis-message">
                      <p>No sentiment analysis available for this interview.</p>
                      <p>Select this interview and click "Analyze Selected" to generate analysis.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for detail view */}
      {loadingDetail && (
        <div className="modal-overlay">
          <div className="loading-spinner">Loading interview details...</div>
        </div>
      )}
    </div>
  );
};

// Helper functions to extract insights from heatmap
function getBiggestPainPoint(heatmap: InterviewDetail['fair_heatmap']): string {
  if (!heatmap?.by_pillar) return 'Findable';
  const entries = Object.entries(heatmap.by_pillar);
  const sorted = entries.sort((a, b) => a[1].overall_sentiment - b[1].overall_sentiment);
  return sorted[0]?.[0] || 'Findable';
}

function getBiggestWin(heatmap: InterviewDetail['fair_heatmap']): string {
  if (!heatmap?.by_pillar) return 'Accessible';
  const entries = Object.entries(heatmap.by_pillar);
  const sorted = entries.sort((a, b) => b[1].overall_sentiment - a[1].overall_sentiment);
  return sorted[0]?.[0] || 'Accessible';
}

export default OrganizationalInterviews;
